import { PhoneArrowUpRightIcon } from '@heroicons/react/24/outline';
import { CountryCode, parsePhoneNumber } from 'libphonenumber-js';
import { AlertCircle, AlertTriangle, CheckCircle, Phone } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { CarrierContactWithTask } from '@/hooks/useCarrierTasksForHoldTransfer';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { useTelephonyProvider } from '@/hooks/useTelephonyProvider';
import { useMatterContext } from '@/providers/MatterProvider';
import { useStartEnhancedBlandCallWorkflowMutation } from '@/services/api/blandService';
import { useGetMatterQuery } from '@/services/api/mattersService';
import { Contact, ContactType } from '@/services/types/task-types';
import { isPhoneNumberValid } from '@/utils/validation';

import { AIHoldAndTransferContactSelector } from './AIHoldAndTransferContactSelector';
import { CallContactSelector } from './CallContactSelector';

type CallMode = 'phone-call' | 'ai-hold-transfer';

export type CallSource =
  | 'header_dialpad_button'
  | 'matter_layout'
  | 'task_header'
  | 'contact_handles_list'
  | 'coverage_contact_modal'
  | 'coverage_card_redesign'
  | 'insurance_card'
  | 'police_call_button'
  | 'treatment_provider'
  | 'check_in_modal'
  | 'claims_filing'
  | 'client_communications'
  | 'loa_chaser_progress'
  | 'loa_chase_status_card';

interface CallFormData {
  contact: Contact | null;
  phoneNumber: string;
}

interface AIHoldAndTransferFormData {
  contact: CarrierContactWithTask | null;
  phoneNumber: string;
  taskId: string | null;
  transferToPhone: string;
}

interface MakeCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialPhoneNumber?: string;
  initialContactLabel?: string;
  initialContactPhone?: string;
  /** Optional matterId - if not provided, will use useMatter() hook */
  matterId?: string;
  /** Optional task ID to link the call to a task */
  taskId?: string;
  /** Initial mode - defaults to 'phone-call' */
  initialMode?: CallMode;
  /** Whether to show the mode selector tabs (default: true) */
  showModeSelector?: boolean;
  /** Source of the call initiation for analytics */
  source?: CallSource;
}

// Helper function to format phone number to E.164
const formatToE164 = (phoneNumber: string): string => {
  try {
    // Try to parse the phone number (defaults to US if no country code)
    const parsed = parsePhoneNumber(phoneNumber, 'US' as CountryCode);
    if (parsed && parsed.isValid()) {
      return parsed.format('E.164');
    }
  } catch (error) {
    console.warn('Phone number parsing failed:', error);
  }

  // If parsing fails, return the original number
  // The backend validation will catch any issues
  return phoneNumber;
};

export const MakeCallModal: React.FC<MakeCallModalProps> = ({
  isOpen,
  onClose,
  initialPhoneNumber,
  initialContactLabel,
  initialContactPhone,
  matterId: matterIdProp,
  taskId,
  initialMode = 'phone-call',
  showModeSelector = true,
  source,
}) => {
  const capture = usePostHogCapture();
  const [mode, setMode] = useState<CallMode>(initialMode);
  const { isRingCentral } = useTelephonyProvider();

  // Get matterId from prop, context, or route params (in order of priority)
  const matterContext = useMatterContext();
  const { matter_id: routeMatterId } = useParams<{ matter_id: string }>();
  const matterId =
    matterIdProp || matterContext?.matterId || routeMatterId || '';
  const { data: matterData } = useGetMatterQuery(matterId, {
    skip: !matterId,
  });

  // AI Hold & Transfer mutation
  const [
    startHoldAndTransfer,
    {
      isLoading: isHoldAndTransferLoading,
      isSuccess: isHoldAndTransferSuccess,
      isError: isHoldAndTransferError,
      error: holdAndTransferError,
    },
  ] = useStartEnhancedBlandCallWorkflowMutation();

  // Phone call form
  const dialpadForm = useForm<CallFormData>({
    defaultValues: {
      contact: null,
      phoneNumber: '',
    },
  });

  // AI Hold & Transfer form
  const holdAndTransferForm = useForm<AIHoldAndTransferFormData>({
    defaultValues: {
      contact: null,
      phoneNumber: '',
      taskId: null,
      transferToPhone: '',
    },
  });

  // Reset mode when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
    }
  }, [isOpen, initialMode]);

  // Prefill phone when opening (phone-call mode)
  useEffect(() => {
    const phone = initialPhoneNumber || initialContactPhone;
    if (isOpen && phone && mode === 'phone-call') {
      dialpadForm.setValue('phoneNumber', phone, {
        shouldValidate: true,
      });
    }
  }, [isOpen, initialPhoneNumber, initialContactPhone, dialpadForm, mode]);

  const dialpadPhoneNumber = dialpadForm.watch('phoneNumber');
  const holdAndTransferTaskId = holdAndTransferForm.watch('taskId');
  const holdAndTransferPhoneNumber = holdAndTransferForm.watch('phoneNumber');

  const isPhoneValid =
    mode === 'phone-call'
      ? isPhoneNumberValid(dialpadPhoneNumber)
      : isPhoneNumberValid(holdAndTransferPhoneNumber);

  const hasPhoneNumber = Boolean(matterData?.firm?.sms_sender_number);

  const isLoading = isHoldAndTransferLoading;
  const error = holdAndTransferError;

  const inlineContact = React.useMemo(() => {
    if (!initialContactLabel && !initialContactPhone) return null;
    return {
      id: 'inline-carrier',
      name: initialContactLabel || 'Carrier',
      phone: initialContactPhone || initialPhoneNumber || '',
      type: ContactType.INSURANCE_CARRIER,
    } as Contact;
  }, [initialContactLabel, initialContactPhone, initialPhoneNumber]);

  const handlePhoneCallSubmit = async (data: CallFormData) => {
    if (!isPhoneNumberValid(data.phoneNumber)) {
      dialpadForm.setError('phoneNumber', {
        type: 'manual',
        message: 'Please enter a valid phone number',
      });
      return;
    }

    // Format the phone number to E.164 before sending
    const formattedPhoneNumber = formatToE164(data.phoneNumber);

    // Open the dialer with the phone number
    window.location.href = `rcapp://r/dialer?number=${encodeURIComponent(formattedPhoneNumber)}`;

    capture('call_initiated', {
      provider: isRingCentral ? 'ringcentral' : 'dialpad',
      mode: 'phone_call',
      source,
      matter_id: matterId,
      task_id: taskId,
    });

    dialpadForm.reset();
    onClose();
  };

  const handleHoldAndTransferSubmit = async (
    data: AIHoldAndTransferFormData,
  ) => {
    if (!data.taskId) {
      toast.error('Please select a carrier contact');
      return;
    }

    if (!isPhoneNumberValid(data.phoneNumber)) {
      toast.error('Invalid phone number');
      return;
    }

    const formattedPhoneNumber = formatToE164(data.phoneNumber);
    const formattedTransferPhone = data.transferToPhone
      ? formatToE164(data.transferToPhone)
      : undefined;

    try {
      await startHoldAndTransfer({
        matterId,
        taskId: data.taskId,
        preferredPhoneNumber: formattedPhoneNumber,
        useHoldAndTransfer: true,
        transferToPhoneOverride: formattedTransferPhone,
      }).unwrap();

      capture('call_initiated', {
        provider: 'bland_ai',
        mode: 'ai_hold_transfer',
        source,
        matter_id: matterId,
        task_id: data.taskId ?? undefined,
      });

      toast.success('AI Hold & Transfer started!', {
        description:
          "AI is calling the carrier. You'll get a Slack notification when a human answers.",
      });

      // Reset form and close modal after short delay
      setTimeout(() => {
        holdAndTransferForm.reset();
        onClose();
      }, 1500);
    } catch (err: unknown) {
      console.error('Failed to start AI Hold & Transfer:', err);
      const errorMessage =
        err && typeof err === 'object' && 'data' in err
          ? (err.data as { error?: string })?.error
          : undefined;
      toast.error('Failed to start AI Hold & Transfer', {
        description: errorMessage || 'Please try again.',
      });
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      dialpadForm.reset();
      holdAndTransferForm.reset();
      onClose();
    }
  };

  const getErrorMessage = () => {
    if (!error) {
      return 'Failed to initiate call';
    }

    if ('data' in error) {
      const errorData = error.data as { error?: string };
      return errorData.error || 'An unknown error occurred';
    }
    return 'Failed to initiate call';
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
        {/* Compact Header */}
        <div className="px-4 pt-4 pb-3 border-b border-zinc-100">
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-50">
              {mode === 'phone-call' ? (
                <Phone className="h-3.5 w-3.5 text-violet-600" />
              ) : (
                <PhoneArrowUpRightIcon className="h-3.5 w-3.5 text-violet-600" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-[13px] font-semibold text-zinc-900 truncate">
                {mode === 'phone-call'
                  ? initialContactLabel
                    ? `Call ${initialContactLabel}`
                    : 'Make a Call'
                  : 'AI Hold & Transfer'}
              </DialogTitle>
              <DialogDescription className="text-[11px] text-zinc-500 mt-0.5">
                {matterData?.name || 'Matter'}
              </DialogDescription>
            </div>
          </div>
        </div>

        {/* Mode Tabs */}
        {showModeSelector && (
          <div className="mx-4 mt-3">
            <div className="flex gap-1 p-0.5 bg-zinc-100 rounded-md">
              <button
                type="button"
                onClick={() => setMode('phone-call')}
                disabled={isLoading}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-[10px] font-medium rounded transition-colors ${
                  mode === 'phone-call'
                    ? 'bg-white text-zinc-900 shadow-sm'
                    : 'text-zinc-600 hover:text-zinc-800'
                } disabled:opacity-50`}
              >
                <Phone className="h-3 w-3" />
                Phone Call
              </button>
              <button
                type="button"
                onClick={() => setMode('ai-hold-transfer')}
                disabled={isLoading}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-[10px] font-medium rounded transition-colors ${
                  mode === 'ai-hold-transfer'
                    ? 'bg-white text-zinc-900 shadow-sm'
                    : 'text-zinc-600 hover:text-zinc-800'
                } disabled:opacity-50`}
              >
                <PhoneArrowUpRightIcon className="h-3 w-3" />
                AI Hold & Transfer
              </button>
            </div>
          </div>
        )}

        {/* Phone Call Mode */}
        {mode === 'phone-call' && (
          <>
            {/* Attribution Notice - Compact */}
            <div className="mx-4 mt-3 flex items-center gap-2 px-2.5 py-1.5 bg-zinc-50 border border-zinc-200 rounded">
              <AlertTriangle className="h-3 w-3 text-zinc-400 flex-shrink-0" />
              <p className="text-[10px] font-medium text-zinc-700 leading-tight">
                Call will be attributed to this matter only
              </p>
            </div>

            {/* Missing Phone Number Warning */}
            {!hasPhoneNumber && (
              <div className="mx-4 mt-2 flex items-center gap-2 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded">
                <AlertCircle className="h-3 w-3 text-amber-500 flex-shrink-0" />
                <p className="text-[10px] font-medium text-amber-700 leading-tight">
                  Phone number not configured for this firm
                </p>
              </div>
            )}

            <Form {...dialpadForm}>
              <form
                onSubmit={dialpadForm.handleSubmit(handlePhoneCallSubmit)}
                className="px-4 pb-4"
              >
                <div className="mt-3">
                  <CallContactSelector
                    form={dialpadForm}
                    matterId={matterId}
                    inlineContact={inlineContact}
                  />
                </div>

                <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-zinc-100">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-2.5 py-1 text-[10px] font-medium text-zinc-700 hover:text-zinc-900 hover:bg-zinc-50 rounded transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      !dialpadPhoneNumber ||
                      !isPhoneValid
                    }
                    className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Phone className="h-2.5 w-2.5" />
                    Call
                  </button>
                </div>
              </form>
            </Form>
          </>
        )}

        {/* AI Hold & Transfer Mode */}
        {mode === 'ai-hold-transfer' && (
          <Form {...holdAndTransferForm}>
            <form
              onSubmit={holdAndTransferForm.handleSubmit(
                handleHoldAndTransferSubmit,
              )}
              className="px-4 pb-4"
            >
              <div className="mt-3">
                <AIHoldAndTransferContactSelector
                  form={holdAndTransferForm}
                  matterId={matterId}
                />
              </div>

              {isHoldAndTransferError && holdAndTransferError && (
                <div className="flex items-center gap-2 mt-3 px-2.5 py-1.5 bg-red-50 border border-red-100 rounded">
                  <AlertCircle className="h-3 w-3 text-red-500 flex-shrink-0" />
                  <span className="text-[10px] font-medium text-red-700">
                    {getErrorMessage()}
                  </span>
                </div>
              )}

              {isHoldAndTransferSuccess && (
                <div className="flex items-center gap-2 mt-3 px-2.5 py-1.5 bg-emerald-50 border border-emerald-100 rounded">
                  <CheckCircle className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                  <span className="text-[10px] font-medium text-emerald-700">
                    AI Hold & Transfer started!
                  </span>
                </div>
              )}

              <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-zinc-100">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isHoldAndTransferLoading}
                  className="px-2.5 py-1 text-[10px] font-medium text-zinc-700 hover:text-zinc-900 hover:bg-zinc-50 rounded transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    !holdAndTransferTaskId ||
                    !holdAndTransferPhoneNumber ||
                    !isPhoneValid ||
                    isHoldAndTransferLoading
                  }
                  className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PhoneArrowUpRightIcon className="h-2.5 w-2.5" />
                  {isHoldAndTransferLoading ? 'Starting...' : 'Start AI Call'}
                </button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
